import { useEffect } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useRouteContext } from "@tanstack/react-router";
import { FiEdit, FiEye, FiPlus, FiSave } from "react-icons/fi";

import Popup from "@/components/shared/Popup";
import { ATTN_TEMPLATE, type ATTN, type ENRL } from "@/models.ts";
import { attnMutationFunctions } from "@/models/chld";
import { useLeaData } from "@/queries.ts";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import ATTNAddEditForm from "@/components/records/child/ATTNAddEditForm";
import { validateDaysExpectedSum } from "@/components/records/child/useATTNFormLogic";

export type ATTNRecordDialogProps = {
  selectedATTN: ATTN | undefined;
  attnDefaults?: Partial<ATTN>;
  attnRecords: ATTN[];
  enrlRecords: ENRL[];
  onClose: () => void;
};

export default function ATTNRecordDialog({
  selectedATTN,
  attnDefaults,
  attnRecords,
  enrlRecords,
  onClose,
}: ATTNRecordDialogProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaData = useLeaData(leaContext.currentUserOrg.esiIdPath).data;

  const initialValues: ATTN =
    selectedATTN ?? { ...ATTN_TEMPLATE, ...attnDefaults };

  const form = useForm<ATTN>({ defaultValues: initialValues });

  useEffect(() => {
    if (selectedATTN) {
      form.reset(selectedATTN);
    } else {
      form.reset({ ...ATTN_TEMPLATE, ...attnDefaults });
    }
    form.clearErrors();
  }, [selectedATTN, attnDefaults, form]);

  const canEdit = leaContext.hasRole("ATTNEdit");
  const formId = "frmATTN";

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form,
      recordType: "ATTN",
      toastMessage: "Attendance record submitted.\nWaiting for validation...",
      onSuccess: onClose,
    });

  const mutateFns = attnMutationFunctions(leaData, setSubmitResult);

  const onSubmit: SubmitHandler<ATTN> = (x) => {
    const sumCheck = validateDaysExpectedSum(x);
    if (sumCheck !== true) {
      form.setError("DaysExpected", {
        type: "manual",
        message: sumCheck,
      });
      return;
    }

    handleSubmitStart();
    mutateFns.handleSave(x, false, "A");
  };

  const headerIcon = selectedATTN ? (
    canEdit ? (
      <FiEdit size={20} />
    ) : (
      <FiEye size={20} />
    )
  ) : (
    <FiPlus size={20} />
  );
  const headerText = selectedATTN
    ? canEdit
      ? "Edit Attendance Record"
      : "View Attendance Record"
    : "Add Attendance Record";
  const submitButtonText = selectedATTN ? "Update" : "Save";

  return (
    <Popup
      show={true}
      header={
        <div
          className="d-flex align-items-center gap-2"
          style={{ fontSize: "1.25rem" }}
        >
          {headerIcon}
          <span>{headerText}</span>
        </div>
      }
      hasButton={false}
      submitFormId={canEdit ? formId : undefined}
      submitButtonText={
        <span className="d-flex align-items-center">
          <FiSave className="me-1" />
          {submitButtonText}
        </span>
      }
      submitButtonLoading={isSubmitting}
      submitButtonLoadingText="Submitting..."
      secondButtonText="Cancel"
      onSecondButtonClick={onClose}
      canEdit={canEdit}
      size="lg"
      onClose={onClose}
    >
      <Form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
        <ATTNAddEditForm
          selectedATTN={selectedATTN}
          form={form}
          attnRecords={attnRecords}
          enrlRecords={enrlRecords}
        />
      </Form>
    </Popup>
  );
}
